<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitDisabled="submitting || loading || loadingStock || !hasStock"
    :submitTitle="submitTitle"
    icon="fa-layer-plus"
    sizeClass="modal-xl"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" v-if="!loading">
      <FormRow label="Stock">
        <FormSearchableSelect ref="stockCodeEntry" v-model="orderItemData.stockId" @input="stockItemUpdated" :options="stockLookupList" placeholder="Stock Code" field="stockId" />
      </FormRow>
      <div v-if="hasStock">
        <Spinner v-if="loadingStock" />
        <div v-if="hasStock && orderItemData.stock">
          <FormRow label="Stock Description">
            <FormInputText v-model="orderItemData.description" placeholder="Description" field="description"></FormInputText>
          </FormRow>
          <FormRow label="Price Group">
            <FormSelect v-model="orderItemData.stockPriceGroupId" :options="stockPriceGroupLookup" placeholder="Price Group" field="stockPriceGroupId" />
          </FormRow>
          <FormRow label="Stamped" v-if="isStampAvailable">
            <FormCheckbox v-model="isStamped" placeholder="Stamped" field="isStamped" />
          </FormRow>
          <FormRow label="Glass" v-if="isStockLinked">
            <FormInputText :value="glassLevelDescription" :readonly="true" field="glassLevelId" :addonSlot="true">
              <template v-slot:addon>
                <FormButton class="btn-sm btn-white" @click="selectGlassLevel"><i class="far fa-layer-plus"></i> Select Glass</FormButton>
              </template>
            </FormInputText>
          </FormRow>
          <FormHorizontalRule />
          <FormRow label="Mark As">
            <FormInputText v-model="orderItemData.mark" placeholder="Mark As" note="Name / Description for this glass item" field="mark"></FormInputText>
          </FormRow>
          <FormRow label="Notes">
            <FormInputTextArea v-model="orderItemData.notes" placeholder="Additional Notes" note="Additional notes for production and processing"></FormInputTextArea>
          </FormRow>
          <FormHorizontalRule />
          <FormRow label="Quantity">
            <FormInputText type="number" v-model="orderItemData.quantity" placeholder="Quantity" field="quantity" />
          </FormRow>
          <FormRow label="Initial Size" :fieldCount="2" v-if="isCreateMode && (isRectangleShape || isOvalShape) && !isStockLinked">
            <template v-slot:field1>
              <FormInputText type="number" v-model="initialHeight" placeholder="Height" prepend="Height" addon="mm" field="initialHeight" />
            </template>
            <template v-slot:field2>
              <FormInputText type="number" v-model="initialWidth" placeholder="Width" prepend="Width" addon="mm" field="initialWidth" />
            </template>
          </FormRow>
          <FormRow label="Initial Size" v-if="isCreateMode && (isCircleShape || isSemiCircleShape)">
            <FormInputText type="number" v-model="initialRadius" placeholder="Radius" prepend="Radius" addon="mm" field="initialRadius" />
          </FormRow>
          <FormRow label="Stock Face Up" v-if="isStockUsingFaces">
            <FormSelect v-model="orderItemData.stockFaceSideUpType" :options="StockFaceTypeLookup" placeholder="Stock Face Up" field="stockFaceSideUpType" />
          </FormRow>
          <FormRow label="Stock Grain Direction" v-if="isStockUsingGrain">
            <FormSelect v-model="orderItemData.grainDirection" :options="StockGrainDirectionTypeLookup" placeholder="Stock Grain Direction" field="grainDirection" />
          </FormRow>

          <FormRow label="Thickness">
            <FormStaticLabel>{{orderItemData.stock.thickness}}mm</FormStaticLabel>
          </FormRow>
          <FormRow label="Unit Price">
            <FormStaticLabel v-if="!orderItemData.unitPriceOverridden">
              {{formatCurrency(orderItemData.unitPrice)}}
              <a class="order-item-editable-button ml-2" @click.prevent="overrideUnitPrice()"><i class="far fa-pencil"></i></a>
            </FormStaticLabel>
            <FormInputText v-if="orderItemData.unitPriceOverridden" type="number" prepend="$" v-model="orderItemData.unitPrice" :addonSlot="true">
              <template v-slot:addon>
                <button data-toggle="dropdown" class="btn btn-white dropdown-toggle" type="button"></button>
                <ul class="dropdown-menu">
                  <DropdownMenuItem icon="fa-undo-alt" @click="resetUnitPrice">Reset Unit Price</DropdownMenuItem>
                </ul>
              </template>
            </FormInputText>
          </FormRow>
        </div>
      </div>

    </FormGroup>

    <GlassLevelSelectPopover
      ref="glassLevelSelectPopover"
      placement="left"
      @selectGlassLevel="glassLevelSelected($event)"
    />
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import FilterUtils from '@/utils/filterUtils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import GlassLevelSelectPopover from '@/modules/inventory/components/glassLevelSelectPopover'

import { InventoryStockTypes, PriceTypes, StockFaceTypes, StockFaceTypeLookup, StockGrainDirectionTypes, StockGrainDirectionTypeLookup, FilterOptionTypes } from '@/store/types'
import { LibraryItemShapeTypes } from '../types'

export default {
  name: 'GlassItemDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode,
    GlassLevelSelectPopover
  },
  props: {
    isCreateMode: Boolean,
    contactId: Number,
    defaultSalesRateId: Number,      // Note: Used if contactId is not set
    editorConfigData: Object
  },
  data() {
    return {
      dialogForm: {},
      orderItemData: {
        stockId: null,
        stockPriceGroupId: null,
        stock: null,
        glassLevelId: null,
        glassLevel: null,
        description: '',
        mark: '',
        notes: '',
        quantity: 1,
        thickness: 0,
        unitPrice: 0,
        unitPriceOverridden: false,
        minChargePrice: 0,
        minChargeQuantity: 0,
        minChargeInvoice: 0,
        grainDirection: StockGrainDirectionTypes.vertical,
        stockFaceSideUpType: StockFaceTypes.smooth
      },
      shapeType: '',
      initialWidth: 1000,
      initialHeight: 1000,
      initialRadius: 500,
      defaultPriceGroupId: 0,
      isStamped: false,
      isStampAvailable: false,
      stockList: [],
      stockLookupList: [],
      priceGroupList: [],
      globalDefaultEdgeworkList: [],
      stockDefaultEdgeworkList: [],
      loading: false,
      loadingStock: false,
      loadingError: '',
      submitting: false,
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.StockFaceTypeLookup = StockFaceTypeLookup
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  watch: {
    'orderItemData.stockId': function() {
      this.loadStockData()
    },
    'orderItemData.stockPriceGroupId': function(newValue, oldValue) {
      this.queryStockPrice()
      this.updateStampConfig()

      if(oldValue !== null)
        this.resetStampConfig()
    }
  },
  mounted() {
    this.loading = true
    this.loadingError = ''

    Promise.all([
      this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.glass }),
      this.$apiService.pricingService.getStockPriceGroupList(),
      this.$apiService.orderEditorService.getOrderEditorDefaultEdgeworkList()
    ])
    .then(results => {
      this.priceGroupList = results[1].data
      this.stockList = results[0].data
      this.globalDefaultEdgeworkList = results[2].data
      this.stockLookupList = this.stockList.map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })

      let defaultPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.isDefault)
      if(defaultPriceGroup)
        this.defaultPriceGroupId = defaultPriceGroup.id
    })
    .catch(err => {
      this.loadingError = 'Error loading data'
    })
    .finally(() => {
      this.loading = false
    })
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Glass Item' : 'Update Glass Item'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    hasStock() {
      return this.orderItemData.stockId != null
    },
    availableStockPriceGroups() {
      if(this.orderItemData.stock && this.orderItemData.stock.glassSalesPriceList) {
        return this.orderItemData.stock.glassSalesPriceList.filter(salesPrice => salesPrice.isActive).map(salesPrice => {
          return {
            id: salesPrice.stockPriceGroupId,
            name: salesPrice.name
          }
        })
      }
      return []
    },
    stockPriceGroupLookup() {
      var isStockLinkedAvailable = this.isCreateMode && this.isRectangleShape
      var isProcessedAvailable = true

      // If already created, only allow stock linked price groups if currently stock linked
      // Once processed is enabled, we can't easily tell if modifications have been made to guarantee it's available for stock only
      if(!this.isCreateMode) {
        let existingPriceGroup = this.priceGroupList.find(pg => pg.id == this.orderItemData.stockPriceGroupId)
        if(existingPriceGroup) {
          isStockLinkedAvailable = existingPriceGroup.isStockLinked
        }
      }

      return this.availableStockPriceGroups
      .filter(priceGroup => {
        let priceGroupInfo = this.priceGroupList.find(pg => pg.id == priceGroup.id)
        if(!priceGroupInfo)
          return false

        return (isProcessedAvailable && !priceGroupInfo.isStockLinked) || (isStockLinkedAvailable && priceGroupInfo.isStockLinked)
      })
      .map(priceGroup => {
        return {
          value: priceGroup.id,
          name: priceGroup.name
        }
      })
    },
    isStockLinked() {
      let stockPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.id == this.orderItemData.stockPriceGroupId)
      if(!stockPriceGroup)
        return false

      return stockPriceGroup.isStockLinked
    },
    glassLevelDescription() {
      if(this.orderItemData.glassLevelId && this.orderItemData.glassLevel)
        return `${this.orderItemData.glassLevel.height} x ${this.orderItemData.glassLevel.width}`

      return 'No glass selected'
    },
    isRectangleShape() {
      return this.shapeType == LibraryItemShapeTypes.rectangle
    },
    isCircleShape() {
      return this.shapeType == LibraryItemShapeTypes.circle
    },
    isSemiCircleShape() {
      return this.shapeType == LibraryItemShapeTypes.semiCircle
    },
    isOvalShape() {
      return this.shapeType == LibraryItemShapeTypes.oval
    },
    isCustomShape() {
      return this.shapeType == LibraryItemShapeTypes.custom
    },
    isStockUsingFaces() {
      if(!this.hasStock)
        return false
      if(!this.orderItemData.stock)
        return false

      return this.orderItemData.stock.isStockUsingFaces
    },
    isStockUsingGrain() {
      if(!this.hasStock)
        return false
      if(!this.orderItemData.stock)
        return false

      return this.orderItemData.stock.isStockUsingGrain
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.dialogForm = { }

      this.shapeType = options.shapeType || LibraryItemShapeTypes.rectangle
      this.initialWidth = options.initialWidth || 1000
      this.initialHeight = options.initialHeight || 1000
      this.initialRadius = options.initialRadius || 500
      this.isStamped = !!options.isStamped

      this.orderItemData.description = options.description || ''
      this.orderItemData.mark = options.mark || ''
      this.orderItemData.notes = options.notes || ''
      this.orderItemData.quantity = parseInt(options.quantity || 1)
      this.orderItemData.unitPrice = options.unitPrice
      this.orderItemData.unitPriceOverridden = !!options.unitPriceOverridden

      this.orderItemData.grainDirection = options.grainDirection || StockGrainDirectionTypes.vertical
      this.orderItemData.stockFaceSideUpType = options.stockFaceSideUpType || StockFaceTypes.smooth

      if(options.glassLevelId) {
        this.orderItemData.glassLevelId = parseInt(options.glassLevelId)
        this.orderItemData.glassLevel = _.clone(options.glassLevel || {})
      }

      if(options.stockId)
        this.orderItemData.stockId = parseInt(options.stockId)
      if(options.stockPriceGroupId)
        this.orderItemData.stockPriceGroupId = parseInt(options.stockPriceGroupId)

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.dialogForm = this.clearFormResponse()

      let stockPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.id == this.orderItemData.stockPriceGroupId)
      let isPriceGroupAvailable = this.availableStockPriceGroups.find(priceGroup => priceGroup.id == this.orderItemData.stockPriceGroupId)

      // Run checks
      // Quantity cannot be <= 0
      // If create mode: Initial size must be valid
      // Stock, pricegroup must be set
      if(this.orderItemData.quantity <= 0)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'quantity', 'Quantity must be greater than 0')
      if(!this.orderItemData.stockId)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'stockId', 'Stock must be set')
      if(!this.orderItemData.stockPriceGroupId || !stockPriceGroup || !isPriceGroupAvailable)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'stockPriceGroupId', 'Price group must be set')

      if(this.isCreateMode && (this.isRectangleShape || this.isOvalShape)) {
        if(parseInt(this.initialHeight || 0) <= 0)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'initialHeight', 'Initial height must be valid')
        if(parseInt(this.initialWidth || 0) <= 0)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'initialWidth', 'Initial width must be valid')
      }
      if(this.isCreateMode && (this.isCircleShape || this.isSemiCircleShape)) {
        if(parseInt(this.initialRadius || 0) <= 0)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'initialRadius', 'Initial radius must be valid')
      }
      if(this.isStockLinked) {
        if(!this.orderItemData.glassLevelId)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'glassLevelId', 'Glass size must be selected')
      }

      if(this.formResponseHasErrors(this.dialogForm))
        return

      // Get the default edgework required for stock/pricegroup combination
      // Stock specific edgework overrides the global setting, even if it turns it off
      var defaultEdgeworkConfigId = 0
      let globalEdgeworkConfig = this.globalDefaultEdgeworkList.find(edgeConfig => edgeConfig.stockPriceGroupId == this.orderItemData.stockPriceGroupId)
      let stockEdgeworkConfig = this.stockDefaultEdgeworkList.find(edgeConfig => edgeConfig.stockPriceGroupId == this.orderItemData.stockPriceGroupId)
      if(globalEdgeworkConfig && globalEdgeworkConfig.orderEditorEdgeworkConfigId)
        defaultEdgeworkConfigId = globalEdgeworkConfig.orderEditorEdgeworkConfigId
      if(stockEdgeworkConfig)
        defaultEdgeworkConfigId = stockEdgeworkConfig.orderEditorEdgeworkConfigId

      var itemUpdateData = {
        stockId: this.orderItemData.stockId,
        stockPriceGroupId: this.orderItemData.stockPriceGroupId,
        description: this.orderItemData.description,
        mark: this.orderItemData.mark,
        notes: this.orderItemData.notes,
        quantity: this.orderItemData.quantity,
        stock: this.orderItemData.stock,
        stockPriceGroup: stockPriceGroup,
        unitPriceOverridden: this.orderItemData.unitPriceOverridden,
        unitPrice: this.orderItemData.unitPrice,
        glassLevelId: this.orderItemData.glassLevelId,
        glassLevel: this.orderItemData.glassLevel,
        defaultEdgeworkConfigId: defaultEdgeworkConfigId,
        grainDirection: this.orderItemData.grainDirection,
        stockFaceSideUpType: this.orderItemData.stockFaceSideUpType,
        isStamped: this.isStampAvailable && this.isStamped
      }

      if(this.isCreateMode) {
        itemUpdateData.height = this.initialHeight
        itemUpdateData.width = this.initialWidth
        itemUpdateData.radius = this.initialRadius
      }

      if(this.isStockLinked && this.orderItemData.glassLevelId) {
        itemUpdateData.height = this.orderItemData.glassLevel.height
        itemUpdateData.width = this.orderItemData.glassLevel.width
      }

      this.modalClosed(itemUpdateData)
      this.hide()
    },
    loadStockData() {
      if(this.orderItemData.stockId == null) {
        this.orderItemData.stock = null
        this.orderItemData.unitPrice = 0
        this.orderItemData.unitPriceOverridden = false
        this.orderItemData.glassLevelId = null
        this.orderItemData.glassLevel = null
        return
      }

      this.loadingStock = true
      this.orderItemData.stock = null
      if(!this.orderItemData.unitPriceOverridden)
        this.orderItemData.unitPrice = 0

      Promise.all([
        this.$apiService.inventoryService.getStock(this.orderItemData.stockId),
        this.$apiService.orderEditorService.getStockOrderEditorDefaultEdgeworkList(this.orderItemData.stockId)
      ])
      .then(results => {

        this.orderItemData.stock = results[0].data
        this.orderItemData.description = this.orderItemData.stock.name
        this.stockDefaultEdgeworkList = results[1].data

        // Set default price group for the stock (if it has pricing available)
        if(this.orderItemData.stockPriceGroupId == null) {
          if(this.orderItemData.stock.glassSalesPriceList) {
            if(this.orderItemData.stock.glassSalesPriceList.find(price => price.isActive && price.stockPriceGroupId == this.defaultPriceGroupId) != null) {
              this.orderItemData.stockPriceGroupId = this.defaultPriceGroupId
            }
          }
        } else {
          this.queryStockPrice()
        }

        // Set default face up if a new glass item
        if(this.isCreateMode && this.orderItemData.stock.isStockUsingFaces) {
          this.orderItemData.stockFaceSideUpType = this.orderItemData.stock.defaultFaceSideUp
        }

      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loadingStock = false
      })
    },
    stockItemUpdated() {
      // Reset some details for the order item
      this.orderItemData.glassLevelId = null
      this.orderItemData.glassLevel = null
    },
    queryStockPrice() {
      if(this.orderItemData.stock == null)
        return

      // Pricing lookup
      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.$props.contactId,
        defaultSalesRateId: this.$props.defaultSalesRateId,
        stockId: this.orderItemData.stockId,
        stockPriceGroupId: this.orderItemData.stockPriceGroupId,
        thickness: this.orderItemData.stock.thickness
      }

      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        if(!this.orderItemData.unitPriceOverridden)
          this.orderItemData.unitPrice = parseFloat(response.data.price.value)

        this.orderItemData.minChargePrice = parseFloat(response.data.price.minChargePrice)
        this.orderItemData.minChargeQuantity = parseFloat(response.data.price.minChargeQuantity)
        this.orderItemData.minChargeInvoice = parseFloat(response.data.price.minChargeInvoice)

        if(response.data.price.error) {
          // Pricing error
          this.orderItemData.priceError = response.data.price.error
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    selectGlassLevel(evt) {
      this.$refs.glassLevelSelectPopover.displayFromElement(evt.target, this.orderItemData.stockId)
    },
    glassLevelSelected(glassLevelInfo) {
      this.orderItemData.glassLevelId = glassLevelInfo.id
      this.orderItemData.glassLevel = _.clone(glassLevelInfo)

      // If the glass has grain direction, change to this glass level's grain direction (based on GlassSize)
      if(this.isStockUsingGrain) {
        // Find the glass size
        if(this.orderItemData.stock.glassSizeList) {
          let glassSize = this.orderItemData.stock.glassSizeList.find(glassSize => {
            if(glassSize.glassLevels && glassSize.glassLevels.some(glassLevel => glassLevel.id == this.orderItemData.glassLevelId))
              return true
            return false
          })

          // Update grain direction
          if(glassSize) {
            this.orderItemData.grainDirection = glassSize.grainDirection || this.orderItemData.grainDirection
          }
        }
      }
    },
    overrideUnitPrice() {
      this.orderItemData.unitPriceOverridden = true
    },
    resetUnitPrice() {
      this.orderItemData.unitPriceOverridden = false
      this.queryStockPrice()
    },
    updateStampConfig() {
      if(!this.editorConfigData)
        return
      if(!this.editorConfigData.orderStampConfig)
        return

      this.isStampAvailable = FilterUtils.itemPassesFilter(this.editorConfigData.orderStampConfig.priceGroupFilter, parseInt(this.orderItemData.stockPriceGroupId))
      if(!this.isStampAvailable) {
        this.isStamped = false
        return
      }
    },
    resetStampConfig() {
      if(!this.isStampAvailable)
        return

      // Stamps are on by default, unless explicitly not required for a contact
      // Auto apply needed?
      if(this.editorConfigData.orderStampConfig.isAutoApply) {
        this.isStamped = true
        if(this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter && this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter.type == FilterOptionTypes.include) {
          this.isStamped = !FilterUtils.itemPassesFilter(this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter, this.contactId)
        }
      }
    }
  }
}
</script>